import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays, Sparkles, Flame } from "lucide-react";

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password);
      nav("/dashboard");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain grid lg:grid-cols-2">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-secondary text-secondary-foreground relative overflow-hidden">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-6 h-6 text-terracotta" strokeWidth={1.75} />
          <span className="font-display text-2xl">
            <span className="text-secondary-foreground">Calen</span><span className="text-honey">Lingo</span>
          </span>
        </div>
        <div className="space-y-6 relative z-10">
          <span
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-honey/20 text-honey border border-honey/40 px-3 py-1 rounded-full"
            data-testid="landing-tagline-pill"
          >
            <Sparkles className="w-3 h-3" strokeWidth={2} /> A word a day &middot; est. 2026
          </span>
          <h1 className="font-display text-5xl xl:text-6xl leading-tight tracking-tight">
            A new word,<br /><span className="text-honey">every single</span> day.
          </h1>
          <p className="text-secondary-foreground/80 max-w-md text-base">
            Turn your calendar into a language teacher. One handpicked word, phrase, or expression lands on your day — with pronunciation, a cultural note, and a quick quiz.
          </p>
          <div className="flex flex-wrap gap-6 pt-2">
            <div className="flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-honey" strokeWidth={1.75} />
              AI-crafted daily lessons
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Flame className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
              Streaks that stick
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CalendarDays className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
              Syncs to Outlook &amp; Google
            </div>
          </div>
        </div>
        <div className="text-xs text-secondary-foreground/50">Learn on your own clock.</div>
        {/* Decorative circles */}
        <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full bg-terracotta/20 blur-3xl" />
        <div className="absolute -left-16 top-40 w-64 h-64 rounded-full bg-honey/20 blur-3xl" />
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={submit}
          className="w-full max-w-md space-y-6 bg-card border rounded-2xl p-8 shadow-sm"
          data-testid={isLogin ? "login-form" : "register-form"}
        >
          <div className="flex items-center gap-2 lg:hidden">
            <CalendarDays className="w-5 h-5 text-terracotta" strokeWidth={1.75} />
            <span className="font-display text-xl">
              <span>Calen</span><span className="text-honey">Lingo</span>
            </span>
          </div>

          <div>
            <h2 className="font-display text-3xl tracking-tight">
              {isLogin ? "Welcome back" : "Create your account"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {isLogin ? "Pick up where you left off." : "One minute to sign up. A lifetime of tiny wins."}
            </p>
          </div>

          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            type="button"
            variant="outline"
            data-testid="google-signin-button"
            onClick={() => {
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href =
                `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full rounded-full h-11 border-2 bg-white hover:bg-muted text-foreground"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative flex items-center">
            <div className="flex-grow border-t border-border" />
            <span className="mx-3 text-xs uppercase tracking-widest text-muted-foreground">or</span>
            <div className="flex-grow border-t border-border" />
          </div>

          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name" data-testid="register-name-input"
                value={name} onChange={(e) => setName(e.target.value)}
                required placeholder="Alex Morgan"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email" type="email"
              data-testid={isLogin ? "login-email-input" : "register-email-input"}
              value={email} onChange={(e) => setEmail(e.target.value)}
              required placeholder="you@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password" type="password" minLength={6}
              data-testid={isLogin ? "login-password-input" : "register-password-input"}
              value={password} onChange={(e) => setPassword(e.target.value)}
              required placeholder="At least 6 characters"
            />
          </div>

          {err && (
            <div
              className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
              data-testid="auth-error"
            >
              {err}
            </div>
          )}

          <Button
            type="submit" disabled={busy}
            data-testid={isLogin ? "login-submit-button" : "register-submit-button"}
            className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta/90 text-white"
          >
            {busy ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            {isLogin ? (
              <>New here?{" "}
                <Link to="/register" className="text-terracotta hover:underline" data-testid="switch-to-register">
                  Create an account
                </Link>
              </>
            ) : (
              <>Already have one?{" "}
                <Link to="/login" className="text-terracotta hover:underline" data-testid="switch-to-login">
                  Sign in
                </Link>
              </>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}
